import * as React from 'react'
import { useState, useCallback, useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import PropTypes from 'prop-types'
import useMediaQuery from '../../hooks/useMediaQuery'
import TailwindScreens from '../../helpers/tailwind'
import {
  GnsLogo,
  IconChatDL,
  IconPhoneDL,
  IconMailDL,
  IconLanguage,
  IconCatalog,
  IconChevronRight,
  IconCufflinks,
  IconNewArrivals,
  IconSale,
  IconRepair,
  IconTrade,
  IconDigitalCatalog,
  IconBlogs,
} from '../Icons'
import AnimatedCartIcon from './AnimatedCartIcon'
import SideCart from './SideCart'
import MenuLink from './HomeMenu/MenuLink'
import { Autocomplete } from '../algolia/Autocomplete'
import { IconMensWatch, IconNecklace, IconSterlingSilver } from '../products/IconsProductPreloader'
import JewelryDropDownMenu from './HomeMenu/JewelryDropDownMenu'
import WatchDropDownMenu from './HomeMenu/WatchDropDownMenu'
import AccessoriesDropDownMenu from './HomeMenu/AccessoriesDropDownMenu'
import LanguageDropDownMenu from './HomeMenu/LanguageDropDownMenu'
import { openChat } from '../../state/popupForms'
import { useDispatch } from 'react-redux'
import EmailLink from '../EmailLink'
import PhoneLink from '../PhoneLink'

const HomeMenu = ({ navbarOpen, setNavbarOpen, h1 = false, mainCategory = null }) => {
  const dispatch = useDispatch()
  const chooseState = (nullableBool, ifNull, ifTrue, ifFalse) => {
    if (nullableBool === null) return ifNull
    return nullableBool ? ifTrue : ifFalse
  }
  const openChatHandler = useCallback(e => {
    e.preventDefault()
    dispatch(openChat())
  }, [])
  const isMd = useMediaQuery(TailwindScreens.md)
  const [dropDown, setDropDown] = useState(0)
  const onBurgerClick = useCallback(() => setNavbarOpen(o => !o), [setNavbarOpen])
  const onMenuLinkClick = useCallback(() => {
    setNavbarOpen(false)
    if (window) {
      window.scrollTo(0, 0)
    }
  }, [setNavbarOpen])

  const handleDropdown = useCallback(
    e => {
      if (!isMd) return
      const dropdown = parseInt(e.target.getAttribute('data-dropdown'))
      setDropDown(dropdown)
    },
    [isMd]
  )
  const handleExpandDropdown = useCallback(
    e => {
      if (isMd) return
      e.preventDefault()
      const id = parseInt(e.target.getAttribute('data-dropdown'))
      setDropDown(id)
    },
    [isMd]
  )

  const searchBox = useRef()
  const openSearchBar = useCallback(
    e => {
      if (isMd) return
      e.preventDefault()
      setNavbarOpen(false)
      setDropDown(d => (d === 3 ? 0 : 3))
      if (dropDown !== 3) {
        searchBox.current.focus()
      }
    },
    [isMd, searchBox, setNavbarOpen]
  )
  useEffect(() => {
    if (!isMd) return
    setNavbarOpen(null)
  }, [isMd, setNavbarOpen])

  const [showCart, setShowCart] = useState(false)
  const [hover, setHover] = useState({
    cart: false,
    menu: false,
  })
  const onCatalogEnter = useCallback(() => setShowCart(false), [])
  const onCartButtonEnter = useCallback(() => setHover(h => ({ ...h, cart: true })), [])
  const onCartButtonLeave = useCallback(() => setHover(h => ({ ...h, cart: false })), [])
  const onMenuEnter = useCallback(() => setHover(h => ({ ...h, menu: true })), [])
  const onMenuLeave = useCallback(() => setHover(h => ({ ...h, menu: false })), [])

  useEffect(() => {
    if (hover.cart) {
      setShowCart(true)
      return
    }
    if (!showCart) return
    if (hover.menu) return
    const timeout = setTimeout(() => setShowCart(false), 200)
    return () => clearTimeout(timeout)
  }, [hover.cart, hover.menu, showCart])

  const LogoTag = h1 ? 'h1' : 'div'

  return (
    <section className="z-[101] print:hidden">
      {/* Top white bar containing logo, contact buttons and query buttons */}
      <div
        className="relative z-[101] w-full border-b bg-white md:px-10 h-16"
        onMouseEnter={onMenuEnter}
        onMouseLeave={onMenuLeave}
      >
        <div className="mx-auto flex w-full max-w-[1366px] flex-col xxs:flex-row items-center justify-between lg:px-0 ">
          {/* This is the Logo */}
          <Link
            onClick={onMenuLinkClick}
            aria-label="Gray and Sons Jewelers"
            to={'/'}
            className="order-2 mx-5 hidden xxs:flex"
          >
            <LogoTag className="w-[calc(8rem+2.5vw)] h-[calc(2rem+1vw)]">
              {h1 && <span className="sr-only">{h1} </span>}
              <GnsLogo
                alt="Pre-owned Watches and Luxury Jewelry by Gray and Sons"
                className="w-full h-full"
              />
            </LogoTag>
          </Link>

          {/* These are the contact buttons */}
          <div className="hidden lg:flex flex-row items-center w-full text-sm justify-start gap-x-5 ">
            <EmailLink className="text-black hover:text-red-600">
              <div className="flex flex-row items-center justify-center">
                <IconMailDL className="h-9 w-9" />
                <p className="hidden xl:flex">&nbsp; E-Mail Us</p>
              </div>
            </EmailLink>
            <button className="text-black hover:text-red-600 group" onClick={openChatHandler}>
              <div className="flex flex-row items-center justify-center relative ">
                <div className="flex flex-row gap-[.05rem] absolute top-3 left-[.25rem] z-20">
                  <div className="group-hover:bg-red-600 h-1 w-1 rounded-full group-hover:chat-dot-blink1 "></div>
                  <div className="group-hover:bg-red-600 h-1 w-1 rounded-full group-hover:chat-dot-blink2"></div>
                  <div className="group-hover:bg-red-600 h-1 w-1 rounded-full group-hover:chat-dot-blink3"></div>
                </div>
                <IconChatDL />
                <p className="hidden xl:flex">&nbsp; Live Chat</p>
              </div>
            </button>
            <PhoneLink className="text-black hover:text-red-600">
              <div className="flex flex-row items-center justify-center">
                <IconPhoneDL />
                <p className="hidden xl:flex">&nbsp; (305) 865 0999</p>
              </div>
            </PhoneLink>
            <div className="circle-highlight group relative z-[101] h-[2rem] w-[2rem] -translate-x-2 hover:text-red-700 ">
              <IconLanguage className="absolute top-[.25rem] left-[.25rem] h-6 w-6" />

              <div className="absolute top-[2rem] left-0 z-40 hidden w-[10rem] flex-col gap-4 rounded-md border border-gray-300 bg-white  px-5 py-2 text-base shadow-lg group-hover:flex">
                <Link
                  onClick={onMenuLinkClick}
                  className="text-black duration-150 hover:font-bold hover:text-red-600"
                  to="/spanish"
                >
                  Spanish
                </Link>
                <Link
                  onClick={onMenuLinkClick}
                  className="text-black duration-150 hover:font-bold hover:text-red-600"
                  to="/russian"
                >
                  Russian
                </Link>
                <Link
                  onClick={onMenuLinkClick}
                  className="text-black duration-150 hover:font-bold hover:text-red-600"
                  to="/portuguese"
                >
                  Portuguese
                </Link>
                <Link
                  onClick={onMenuLinkClick}
                  className="text-red-700 hover:font-bold duration-150"
                  to="/locations/"
                >
                  Areas we Serve 
                </Link>
              </div>
            </div>
          </div>

          {/* These are the Query buttons */}
          <div className=" order-3 w-full xxs:max-w-[12rem] xs:gap-5 sm:max-w-[16rem] md:max-w-none flex flex-row items-center justify-around non-hover:justify-between xs:justify-end gap-x-2 py-4 px-5 xxs:px-0 xxs:mr-5 xl:mr-0 ">
            <Autocomplete dropDown={dropDown} openSearchBar={openSearchBar} />

            <div className="catalog-icon group relative cursor-pointer group hidden sm:block">
              <Link
                onMouseEnter={onCatalogEnter}
                //This is where we need to remove the onmouseover and onmouseout from the catalog button
                className="text-black group-hover:text-red-600  "
                to="/register-browse-learn"
              >
                <IconCatalog />
              </Link>

              <div className="absolute top-7 -right-3 group-hover:p-2">
                <div className="z-40 hidden w-60 flex-col gap-4 rounded-md border border-gray-300 bg-white p-5 px-5 py-4 text-base shadow-lg group-hover:flex pointer-fine:space-y-0 space-y-2">
                  {/* Catalog, blog and registration links */}
                  <Link
                    onClick={onMenuLinkClick}
                    className="text-black duration-150 hover:font-bold hover:text-red-600"
                    to="/digital-catalog"
                  >
                    Browse Digital Catalog
                  </Link>
                  <Link
                    onClick={onMenuLinkClick}
                    className="text-black duration-150 hover:font-bold hover:text-red-600"
                    to="/catalog-request"
                  >
                    Request Printed Catalog
                  </Link>
                  <Link
                    onClick={onMenuLinkClick}
                    className="text-black duration-150 hover:font-bold hover:text-red-600"
                    to="/blog"
                  >
                    Read Our Blogs
                  </Link>
                </div>
              </div>
            </div>

            <div className="text-center relative">
              <span className="sr-only">Cart</span>
              <AnimatedCartIcon onMouseEnter={onCartButtonEnter} onMouseLeave={onCartButtonLeave} />
              {showCart && <SideCart />}
            </div>

            <button
              className="w-[25px] h-[20px] relative right-0 top-0 lg:hidden order-4 cursor-pointer"
              onClick={onBurgerClick}
              aria-label="Menu"
            >
              <span
                className={
                  'burger-line ' +
                  chooseState(navbarOpen, 'line1-initial', 'line1-close', 'line1-open')
                }
              />
              <span
                className={
                  'burger-line ' +
                  chooseState(navbarOpen, 'line2-initial', 'line2-close', 'line2-open')
                }
              />
              <span
                className={
                  'burger-line ' +
                  chooseState(navbarOpen, 'line3-initial', 'line3-close', 'line3-open')
                }
              />
            </button>
          </div>
        </div>
      </div>
      {/* This is the slide-in search bar for Mobile devices */}
      <form
        role="search"
        action={'/search/'}
        id="mobile-search-form"
        className={
          (dropDown === 3 ? '' : 'translate-y-[-4.15rem]  ') +
          'md:hidden group absolute left-0 z-40 flex h-14 w-full flex-row justify-end border-b bg-white transition-all duration-500 ease-out px-2'
        }
        onSubmit={event => {
          event.preventDefault()
          if (typeof window === 'undefined') return
          window.location.href = `/search/#search=${searchBox.current.value}`
        }}
      >
        <input
          ref={searchBox}
          aria-label="Search"
          name={'q'}
          className="search-input-style mx-3 w-full border-b-[1px] border-red-700"
          placeholder={'Search Gray and Sons'}
        />
      </form>

      {/* This is the main menu for desktop devices */}
      <nav
        className={
          (navbarOpen ? '' : 'hidden lg:flex ') +
          'absolute flex flex-col left-0 right-0 bottom-0 top-[65px] overflow-x-hidden bg-white lg:bg-red-800 lg:relative lg:top-0 lg:overflow-visible z-40'
        }
        data-dropdown={0}
        onMouseLeave={handleDropdown}
        itemScope
        itemType="https://schema.org/SiteNavigationElement"
      >
        <div className="mx-auto w-full h-full overflow-y-auto flex flex-col text-sm xl:text-base max-w-[1366px] lg:flex-nowrap  uppercase z-[100] lg:h-auto lg:flex-row lg:justify-between lg:space-y-0 lg:py-0">
          <MenuLink
            to="/what-s-new/"
            onClick={onMenuLinkClick}
            onMouseEnter={handleDropdown}
            data-dropdown={0}
            isActive={mainCategory?.url === '/what-s-new'}
          >
            <IconNewArrivals className="lg:hidden w-7 h-7 animate-none text-gray-500" />
            New Arrivals
          </MenuLink>
          <MenuLink
            to="/fine-watches/"
            onClick={handleExpandDropdown}
            onMouseEnter={handleDropdown}
            data-dropdown={1}
            isActive={mainCategory?.url === '/fine-watches'}
            className={dropDown === 1 ? 'text-red-700 ' : 'text-black'}
          >
            <IconMensWatch
              className={
                (dropDown === 1 ? 'text-red-700 stroke-red-700' : 'text-gray-500 stroke-gray-500') +
                ' lg:hidden w-7 h-7 animate-none stroke-[4px] pointer-events-none '
              }
            />
            Luxury Watches
            <IconChevronRight className="lg:hidden w-3 h-3 absolute right-3" />
          </MenuLink>
          <MenuLink
            to="/jewelry/"
            onClick={handleExpandDropdown}
            onMouseEnter={handleDropdown}
            data-dropdown={2}
            isActive={mainCategory?.url === '/jewelry'}
            className={dropDown === 2 ? 'text-red-700 ' : 'text-black'}
          >
            <IconNecklace
              className={
                (dropDown === 2
                  ? 'text-red-700 stroke-red-700'
                  : 'text-gray-500 stroke-gray-500 ') +
                ' lg:hidden w-7 h-7 animate-none stroke-[4px] stroke-gray-500 pointer-events-none'
              }
            />
            Fine Jewelry
            <IconChevronRight className="lg:hidden w-3 h-3 absolute right-3" />
          </MenuLink>
          <MenuLink
            to="/sterlingsilver/"
            onMouseEnter={handleDropdown}
            data-dropdown={0}
            onClick={onMenuLinkClick}
            isActive={mainCategory?.url === '/sterlingsilver'}
          >
            <IconSterlingSilver className="lg:hidden w-7 h-7 animate-none text-gray-500 stroke-[4px] stroke-gray-500" />
            Silver Flatware
          </MenuLink>
          <MenuLink
            to="/accessories/"
            onClick={handleExpandDropdown}
            onMouseEnter={handleDropdown}
            data-dropdown={4}
            isActive={mainCategory?.url === '/accessories'}
            className={dropDown === 4 ? 'text-red-700 ' : 'text-black  '}
          >
            <IconCufflinks className=" lg:hidden w-7 h-7 animate-none pointer-events-none" />
            Accessories
            <IconChevronRight className="lg:hidden w-3 h-3 absolute right-3" />
          </MenuLink>
          <MenuLink
            to="/sale/"
            onMouseEnter={handleDropdown}
            data-dropdown={0}
            onClick={onMenuLinkClick}
            isActive={mainCategory?.url === '/sale'}
          >
            <IconSale className="lg:hidden w-7 h-7 animate-none text-gray-500" />
            Sale
          </MenuLink>

          <MenuLink
            to="/repairs/"
            onMouseEnter={handleDropdown}
            data-dropdown={0}
            onClick={onMenuLinkClick}
            isActive={mainCategory?.url === '/repairs'}
            className="text-white bg-gray-600 lg:bg-transparent"
          >
            <IconRepair className="lg:hidden w-7 h-7 animate-none text-white" />
            Repairs
          </MenuLink>
          {/* eslint-disable-next-line */}
          <MenuLink
            to="/sell-to-us"
            onMouseEnter={handleDropdown}
            data-dropdown={0}
            onClick={onMenuLinkClick}
            isActive={mainCategory?.url === '/repairs'}
            className="text-white bg-gray-600 lg:bg-transparent"
          >
            <IconRepair className="lg:hidden w-7 h-7 animate-none text-white" />
            TRADE / SELL <span className="md:hidden">/ CONSIGN</span>
          </MenuLink>

          <MenuLink
            to="/digital-catalog/"
            onMouseEnter={handleDropdown}
            data-dropdown={0}
            onClick={onMenuLinkClick}
            isActive={mainCategory?.url === '/digital-catalog/'}
            MobileOnly={true}
            className="text-white bg-gray-600 lg:bg-transparent"
          >
            <IconDigitalCatalog className="lg:hidden w-7 h-6 animate-none text-white" />
            Digital Catalog
          </MenuLink>
          <MenuLink
            to="/blog/"
            onMouseEnter={handleDropdown}
            data-dropdown={0}
            onClick={onMenuLinkClick}
            isActive={mainCategory?.url === '/blog/'}
            MobileOnly={true}
            className="text-white bg-gray-600 lg:bg-transparent"
          >
            <IconBlogs className="lg:hidden w-7 h-6 animate-none text-white " />
            Blogs
          </MenuLink>
          <MenuLink
            to="/accessories/"
            onClick={handleExpandDropdown}
            onMouseEnter={handleDropdown}
            data-dropdown={5}
            isActive={mainCategory?.url === '/accessories'}
            MobileOnly={true}
            className="text-white bg-gray-600 lg:bg-transparent"
          >
            <IconLanguage className="lg:hidden w-7 h-6 animate-none text-white pointer-events-none" />
            Language <IconChevronRight className="lg:hidden w-3 h-3 absolute right-3" />
          </MenuLink>
          <div className="grow w-full bg-gray-600 lg:hidden" />
        </div>

        {/* Watches Dropdown Options */}
        <WatchDropDownMenu
          dropDown={dropDown}
          setDropDown={setDropDown}
          onMenuLinkClick={onMenuLinkClick}
        />

        {/* Jewelry Dropdown Options */}
        <JewelryDropDownMenu
          dropDown={dropDown}
          setDropDown={setDropDown}
          onMenuLinkClick={onMenuLinkClick}
        />

        {/* Accessories Dropdown Options */}
        <AccessoriesDropDownMenu
          dropDown={dropDown}
          setDropDown={setDropDown}
          onMenuLinkClick={onMenuLinkClick}
          handleExpandDropdown={handleExpandDropdown}
        />

        {/* Language Dropdown Options */}
        <LanguageDropDownMenu
          dropDown={dropDown}
          setDropDown={setDropDown}
          onMenuLinkClick={onMenuLinkClick}
          handleExpandDropdown={handleExpandDropdown}
        />
      </nav>
    </section>
  )
}

HomeMenu.propTypes = {
  h1: PropTypes.string,
  menuActive: PropTypes.number,
  setMenuActive: PropTypes.func,
  setNavbarOpen: PropTypes.func,
  navbarOpen: PropTypes.bool,
  mainCategory: PropTypes.object,
}

export default HomeMenu
